import { useTranslation } from 'react-i18next';
import { isDrawnOpening, resolveMatchType } from '@draughts/shared';
import type { ChallengeColor, GameMode, MatchLegs, MatchTypeParticipant, OpeningMode } from '@draughts/shared';
import { variantShortLabel } from '../../utils/variant-labels';
import { variantPill } from '../../utils/variant-styling';
import { CADENCE_ICONS, parseTimeControl } from '../../utils/cadence-icons';
import { colorLeading, gameTypeLeading, gameModeLeading, openingLeading, legsLeading } from '../../utils/challenge-icons';
import { openingOptionLabel } from '../../utils/opening-labels';
import { matchTypeLabel, matchTypeValue, knownMatchTypeLabel, gameModeLabel } from '../../utils/challenge-labels';
import './ChallengeDetails.css';

interface ChallengeDetailsProps {
  variant: string;
  timeControl: string;
  /** Challenger's own chosen side (undefined on legacy challenges → random). */
  color?: ChallengeColor;
  /** Who may play, and whether it counts. Absent on challenges made before it existed. */
  matchType?: string;
  rated?: boolean;
  gameMode?: GameMode;
  opening?: OpeningMode;
  /** 2 = andata e ritorno. Shown when it says something: see the row below. */
  legs?: MatchLegs;
  /**
   * L'altro giocatore, quando si sa chi è (Sfida diretta).
   *
   * Con un destinatario in carne e ossa «Valutata/Amichevole» non è un tipo di
   * partita, è una domanda senza risposta: la risposta c'è già, e dipende solo da
   * se quella persona ha un punteggio da muovere.
   *
   * ASSENTE per le due schermate del LINK (ChallengeLinkModal, ChallengeInvitePage):
   * lì chi raccoglierà l'invito non si sa ancora, e «Valutata/Amichevole» è l'unica
   * cosa vera che si possa dire. Per questo si entra e non si esce: chi non passa
   * niente descrive la partita come ha sempre fatto.
   */
  opponent?: MatchTypeParticipant;
}

/**
 * Read-only summary of what a challenge is: variant, cadence, side, rated or
 * casual, plus the rows that only appear when they say something (a non-normal
 * game mode, a drawn opening, a return leg).
 *
 * Shared by every screen that shows a challenge the player did not configure
 * themselves — the outgoing-challenge modal, the challenge-link modal and the
 * invite page a friend lands on from WhatsApp — so all three describe the same
 * settings in the same order and wording.
 */
function ChallengeDetails({ variant, timeControl, color, matchType, rated, gameMode, opening, legs, opponent }: ChallengeDetailsProps) {
  const { t } = useTranslation();

  // `null` finché non si sa con chi si gioca: allora, e solo allora, il tipo
  // partita si dice per nome (e senza il «(senza ospiti)», che è il cartello
  // sulla porta e qui la porta non c'è).
  const tipoNoto = opponent ? resolveMatchType(matchTypeValue(matchType, rated), opponent) : null;

  const timeControlLabel = (tc: string): string => {
    if (tc === 'none') return t('game.timeControls.noTimeLimit');
    const parts = tc.split('-');
    if (parts.length === 2 && parts[0] && parts[1]) {
      const category = parts[0];
      const time = parts[1];
      return `${time} (${category.charAt(0).toUpperCase() + category.slice(1)})`;
    }
    return tc;
  };

  const colorLabel = (c: string | undefined) =>
    c === 'white' ? t('game.clock.white') : c === 'black' ? t('game.clock.black') : t('online.challenge.colorRandom');

  const openingLabel = (o: OpeningMode) => openingOptionLabel(t, o, variant);

  return (
    <div className="challenge-details">
      <div className="detail-item">
        <span className="detail-label">{t('home.variant')}:</span>
        <span className="detail-value">
          {variantPill(variant).flag}
          {variantShortLabel(t, variant)}
        </span>
      </div>
      <div className="detail-item">
        <span className="detail-label">{t('home.timeControl')}:</span>
        <span className="detail-value">
          {CADENCE_ICONS[parseTimeControl(timeControl)?.category ?? '']}
          {timeControlLabel(timeControl)}
        </span>
      </div>
      <div className="detail-item">
        <span className="detail-label">{t('online.challenge.color')}:</span>
        <span className="detail-value">
          {colorLeading(color ?? 'random')}
          {colorLabel(color)}
        </span>
      </div>
      <div className="detail-item">
        <span className="detail-label">{t('online.challenge.gameType')}:</span>
        <span className="detail-value">
          {gameTypeLeading(tipoNoto ?? matchTypeValue(matchType, rated))}
          {tipoNoto ? knownMatchTypeLabel(t, tipoNoto) : matchTypeLabel(t, matchType, rated)}
        </span>
      </div>
      {gameMode && gameMode !== 'normal' && (
        <div className="detail-item">
          <span className="detail-label">{t('online.challenge.mode')}:</span>
          <span className="detail-value">
            {gameModeLeading(gameMode)}
            {gameModeLabel(t, gameMode)}
          </span>
        </div>
      )}
      {opening && isDrawnOpening(opening) && (
        <div className="detail-item">
          <span className="detail-label">{t('online.challenge.opening')}:</span>
          <span className="detail-value">
            {openingLeading(opening)}
            {openingLabel(opening)}
          </span>
        </div>
      )}
      {/* Le manche non dipendono più dall'apertura. Si dicono quando c'è qualcosa
          da dire: il ritorno chiesto, oppure una pescata giocata una volta sola —
          che è il caso in cui «solo andata» spiega perché la partita è amichevole.
          Una mossa libera in partita singola è la partita ordinaria: muta. */}
      {((opening && isDrawnOpening(opening)) || legs === 2) && (
        <div className="detail-item">
          <span className="detail-label">{t('online.challenge.legs')}:</span>
          <span className="detail-value">
            {legsLeading(legs ?? 2)}
            {legs === 1 ? t('online.challenge.legsOne') : t('online.challenge.legsTwo')}
          </span>
        </div>
      )}
    </div>
  );
}

export default ChallengeDetails;
